import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, PieChart, Paperclip, ArrowRight, Ban, Calendar } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { BUDGETS_DATA, BUDGETS_CHAT_RESPONSES } from '../../data/budgetsData';

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border;
  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Finn · {item.time}</p>
      </div>
    </div>
  );
};

const V4FinanceBudgets = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [dateView, setDateView] = useState('monthly');
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || BUDGETS_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleCta = (label, detail) => {
    sendMessage(`${label} — ${detail}`, { text: `I'll handle "${label}" for ${detail}. Setting that up now and will confirm once ready.\n\nAnything else you need?` });
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Finn about budgets..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="budgets-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.finn} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Finn</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="budgets-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="budgets-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="budgets-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Budgets</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.finn} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="budgets-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <PieChart className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Budgets</p>
          </div>
          <button onClick={() => handleCta('New Budget Request', 'Submit a new budget allocation request')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="budgets-new-btn">
            <PieChart className="w-3.5 h-3.5" /> New Budget
          </button>
        </div>
        <div className="flex items-center gap-1">
          {[{ key: 'monthly', label: 'Monthly' }, { key: 'yearly', label: 'Yearly' }, { key: 'custom', label: 'Custom Range' }].map(v => (
            <button key={v.key} onClick={() => setDateView(v.key)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-colors ${dateView === v.key ? (isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20') : `${border} ${ts} ${hoverBg}`}`}
              data-testid={`budgets-view-${v.key}`}
            >
              {v.key === 'custom' && <Calendar className="w-3 h-3" />}{v.label}
            </button>
          ))}
          <span className={`text-[10px] ml-2 ${ts}`}>{dateView === 'monthly' ? 'Jan 2025' : dateView === 'yearly' ? 'FY25' : 'Jan 1 – Feb 27, 2025'}</span>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        <div className="grid grid-cols-6 gap-3">
          {BUDGETS_DATA.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Department Budgets */}
        <div className={`${cardBg} border ${border} rounded-xl overflow-hidden`}>
          <div className={`grid grid-cols-[2fr_1.2fr_1.2fr_1fr_1fr_1fr] items-center px-4 py-2 border-b ${border}`}>
            <span className={`text-[8px] font-semibold ${ts}`}>Department</span>
            <span className={`text-[8px] font-semibold text-center ${ts}`}>Allocated</span>
            <span className={`text-[8px] font-semibold text-center ${ts}`}>Spent</span>
            <span className={`text-[8px] font-semibold ${ts}`}>Progress</span>
            <span className={`text-[8px] font-semibold text-center ${ts}`}>Status</span>
            <span className={`text-[8px] font-semibold text-right ${ts}`}>Action</span>
          </div>
          {BUDGETS_DATA.departments.map(dept => {
            const pct = Math.round((dept.spent / dept.allocated) * 100);
            const isOver = dept.trend === 'over';
            const isUnder = dept.trend === 'under';
            const barColor = isOver ? 'bg-red-500/50' : isUnder ? 'bg-emerald-500/40' : 'bg-indigo-500/40';
            return (
              <div key={dept.name} className={`grid grid-cols-[2fr_1.2fr_1.2fr_1fr_1fr_1fr] items-center px-4 py-2.5 border-b ${border} last:border-0 ${isOver ? (isDark ? 'bg-red-500/5' : 'bg-red-50/30') : ''}`} data-testid={`budget-dept-${dept.name.toLowerCase()}`}>
                <div>
                  <span className={`text-[10px] font-medium ${tp}`}>{dept.name}</span>
                  <span className={`text-[8px] ml-1.5 ${ts}`}>{dept.lead}</span>
                </div>
                <span className={`text-[10px] text-center ${tp}`}>${(dept.allocated / 1000).toFixed(0)}K</span>
                <span className={`text-[10px] text-center font-medium ${isOver ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>${(dept.spent / 1000).toFixed(0)}K</span>
                <div className="px-1">
                  <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`}>
                    <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                  <span className={`text-[8px] ${isOver ? (isDark ? 'text-red-400' : 'text-red-500') : ts}`}>{pct}%</span>
                </div>
                <div className="flex justify-center">
                  <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${isOver ? (isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-50 text-red-600') : isUnder ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600') : (isDark ? 'bg-white/[0.04] text-white/50' : 'bg-black/[0.03] text-slate-500')}`}>
                    {isOver ? 'Over' : isUnder ? 'Under' : 'On Track'}
                  </span>
                </div>
                <div className="flex justify-end">
                  {isOver ? (
                    <button onClick={() => handleCta('Review Budget', dept.name)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap bg-amber-500/10 text-amber-400 hover:bg-amber-500/20`}>
                      <AlertTriangle className="w-3 h-3" /> Review
                    </button>
                  ) : (
                    <button onClick={() => handleCta('View Details', dept.name)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
                      <ArrowRight className="w-3 h-3" /> Details
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Flagged Line Items */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
            <p className={`text-xs font-semibold ${tp}`}>Flagged Line Items</p>
            <span className={`text-[9px] ${ts}`}>{BUDGETS_DATA.flaggedItems.length} items</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {BUDGETS_DATA.flaggedItems.map(f => {
              const isHigh = f.severity === 'high';
              const cardBorder = isHigh ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : (isDark ? 'border-amber-500/10' : 'border-amber-200/40');
              const fCardBg = isHigh ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-amber-950/10' : 'bg-amber-50/30');
              return (
                <div key={f.id} className={`${fCardBg} border ${cardBorder} rounded-xl p-3`} data-testid={`flagged-${f.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-semibold ${tp}`}>{f.department}</span>
                      <span className={`text-[9px] ${ts}`}>{f.category}</span>
                    </div>
                    <span className={`text-[10px] font-bold ${isHigh ? (isDark ? 'text-red-400' : 'text-red-600') : (isDark ? 'text-amber-400' : 'text-amber-600')}`}>{f.variance}</span>
                  </div>
                  <div className="flex items-center gap-4 mb-1.5">
                    <span className={`text-[9px] ${ts}`}>Budget: ${(f.allocated / 1000).toFixed(0)}K</span>
                    <span className={`text-[9px] font-medium ${isHigh ? (isDark ? 'text-red-400' : 'text-red-500') : tp}`}>Actual: ${(f.actual / 1000).toFixed(0)}K</span>
                  </div>
                  <p className={`text-[9px] leading-relaxed ${ts}`}>{f.reason}</p>
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                    <button onClick={() => handleCta('Cap Spend', `${f.department} — ${f.category}`)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isHigh ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' : `${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}`}>
                      <Ban className="w-3 h-3" /> Cap Spend
                    </button>
                    <button onClick={() => handleCta('Reallocate Budget', `${f.department} — ${f.category}`)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
                      <ArrowRight className="w-3 h-3" /> Reallocate
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.finn} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Finn's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {BUDGETS_DATA.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4FinanceBudgets;
